import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  Trophy, Target, Eye, Globe, LineChart, Clock, Calendar, TrendingUp, Search,
  BarChart3, ArrowLeftRight, Medal, TrendingDown, Handshake, Crown, Gift,
  Shield, ArrowDown, ArrowUp, ClipboardList, CreditCard, Landmark, CheckCircle, Rocket, Bitcoin,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';

// Scroll-reveal: fades/slides sections in as the visitor scrolls to them.
// Applied broadly (no need to touch every section's JSX) by tagging a
// standard set of content blocks after mount and observing them.
function useScrollReveal() {
  useEffect(() => {
    const groups = document.querySelectorAll('.grid-2, .grid-3, .grid-4');
    groups.forEach(group => {
      Array.from(group.children).forEach((el, i) => {
        el.classList.add('reveal');
        el.style.setProperty('--reveal-delay', `${(i % 4) * 0.09}s`);
      });
    });
    const targets = document.querySelectorAll(
      '.section-header, .plan-table-wrap, .table-wrap, .package-card'
    );
    targets.forEach(el => el.classList.add('reveal'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// Counts a number up from its starting value to `end` once, on mount —
// kept available for future use elsewhere on the page, currently unused
// after the hero dashboard card was replaced with the background slideshow.

// Cycles through the hero background images automatically, crossfading
// one into the next — like a slideshow. Pauses (holds on the first image)
// if the visitor has reduced-motion enabled.
const heroSlides = [
  'mlm1.jpg',
  'mlm2.png',
  'mlm3.png',
];

function useHeroSlideshow(images, intervalMs = 5000) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIndex(i => (i + 1) % images.length), intervalMs);
    return () => clearInterval(id);
  }, [images, intervalMs]);
  return index;
}

const tickerInstruments = [
  { pair: 'EUR/USD', v: '1.0842', up: true },
  { pair: 'GBP/JPY', v: '190.62', up: false },
  { pair: 'XAU/USD', v: '2,412.5', up: true },
  { pair: 'BTC/USD', v: '61,204', up: true },
  { pair: 'USD/INR', v: '83.41', up: false },
  { pair: 'CRUDE OIL', v: '78.36', up: true },
  { pair: 'ETH/USD', v: '3,014', up: false },
  { pair: 'NIFTY 50', v: '24,812', up: true },
];

const compoundingData = [
  { pkg: '₹1,000', d30: '₹347', d60: '₹816', d90: '₹1,448', d180: '₹4,995', d365: '₹36,783' },
  { pkg: '₹5,000', d30: '₹1,739', d60: '₹4,083', d90: '₹7,243', d180: '₹24,979', d365: '₹1,83,917' },
  { pkg: '₹10,000', d30: '₹3,478', d60: '₹8,166', d90: '₹14,486', d180: '₹49,958', d365: '₹3,67,834' },
  { pkg: '₹20,000', d30: '₹6,956', d60: '₹16,333', d90: '₹28,972', d180: '₹99,916', d365: '₹7,35,668' },
  { pkg: '₹50,000', d30: '₹17,392', d60: '₹40,834', d90: '₹72,431', d180: '₹2,49,790', d365: '₹18,39,170' },
  { pkg: '₹1,00,000', d30: '₹34,784', d60: '₹81,669', d90: '₹1,44,863', d180: '₹4,99,580', d365: '₹36,78,340' },
];

const strategies = [
  { icon: Calendar, title: 'Intraday Trading', desc: 'Positions are opened and closed on the same trading day.' },
  { icon: TrendingUp, title: 'Swing Trading', desc: 'Trades held for several days or weeks to benefit from medium-term market trends.' },
  { icon: Search, title: 'Scalping', desc: 'A fast-paced strategy involving multiple small trades throughout the day.' },
  { icon: Target, title: 'Position Trading', desc: 'A long-term approach where trades are held for weeks, months, or even years.' },
];

const services = [
  { icon: BarChart3, title: 'Stock Trading', desc: 'Buy & Sell company shares for profit.' },
  { icon: ArrowLeftRight, title: 'Forex Trading', desc: 'Trade global currency pairs (EUR/USD, GBP/JPY).' },
  { icon: Bitcoin, title: 'Cryptocurrency', desc: 'Invest in Bitcoin, Ethereum & other digital assets.' },
  { icon: Medal, title: 'Commodity Trading', desc: 'Gold, Silver, Crude Oil, Natural Gas & Agricultural Products.' },
  { icon: TrendingDown, title: 'Index Trading', desc: 'Trade market indices for diversified investment opportunities.' },
];

const incomeTypes = [
  { icon: TrendingUp, title: 'ROI Income', desc: '1% daily compounding on your package amount (Mon-Sat)' },
  { icon: Handshake, title: 'Direct Income', desc: 'Level 1: 7%, Level 2: 2%, Level 3: 1% on direct referral packages' },
  { icon: Trophy, title: 'Daily ROI Level', desc: 'Earn from your team ROI up to 30 levels deep' },
  { icon: Crown, title: 'Royalty Income', desc: 'Daily royalty based on team business volume & members' },
  { icon: Gift, title: 'Reward Income', desc: 'One-time rewards based on achieving business milestones' },
];

export default function Home() {
  useScrollReveal();
  const heroSlide = useHeroSlideshow(heroSlides);

  return (
    <>
      <Navbar />

      {/* LIVE MARKET TICKER */}
      <div className="ticker-strip">
        <div className="ticker-track">
          {[...tickerInstruments, ...tickerInstruments].map((t, i) => (
            <span key={i} className={`ticker-item ${t.up ? 'up' : 'down'}`}>
              <span className="pair">{t.pair}</span> {t.v}
              {t.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            </span>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="hero">
        {/* Background slideshow — 3 images crossfading on a timer */}
        <div className="hero-bg-slideshow">
          {heroSlides.map((src, i) => (
            <div
              key={src}
              className={`hero-bg-slide ${i === heroSlide ? 'is-active' : ''}`}
              style={{ backgroundImage: `url(${src})` }}
            />
          ))}
          <div className="hero-bg-overlay" />
        </div>

        <div className="hero-particles">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="particle" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 6}s`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              opacity: Math.random() * 0.6 + 0.2
            }} />
          ))}
        </div>

        <div className="hero-grid hero-grid-solo">
          <div>
<h1>Build Your Network. Grow Your Income.<br /><span className="glow hero-daily-line">Shape Your Future.</span></h1>
           <p className="hero-sub">
            Build your network, unlock new opportunities, and grow together with a smarter approach to modern MLM business. Our platform is designed to help you connect with people, develop your team, and build a strong foundation for long-term growth. Start your journey today and turn your vision into meaningful opportunities through teamwork, consistency, and continuous growth.
            </p>
            <div className="hero-actions">
              <Link to="/register" className="btn btn-primary btn-lg">Join Now Free</Link>
              <a href="#about" className="btn btn-outline btn-lg">See How It Works</a>
            </div>
            <div className="hero-stat-row">
              {[
                { val: '1%', label: 'Daily ROI' },
                { val: '365', label: 'Days Valid' },
                { val: '30', label: 'Income Levels' },
                { val: '₹500', label: 'Min Package' },
              ].map(stat => (
                <div key={stat.label}>
                  <div className="stat-val">{stat.val}</div>
                  <div className="stat-lbl">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="section section-alt section-relative" id="about">
        <div className="glow-orb-field">
          <div className="glow-orb glow-orb-gold"></div>
          <div className="glow-orb glow-orb-blue"></div>
        </div>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">ABOUT <span>US</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2" style={{ alignItems: 'center', gap: 48 }}>
            <div>
              {[
                'Triveni Pay is dedicated to offering a reliable Forex investment and trading platform.',
                'Setting itself apart through a meticulous and knowledge-driven strategy, the company aims to deliver optimal returns while instilling confidence in investors.',
                'Additionally, Triveni Pay concentrates on the financial intricacies and technical fundamentals of Forex assets.'
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                  <div style={{ width: 3, background: 'var(--primary)', borderRadius: 2, flexShrink: 0 }}></div>
                  <p style={{ color: 'var(--text-light)', lineHeight: 1.8, fontWeight: 500, textTransform: 'uppercase', fontSize: '0.95rem' }}>{text}</p>
                </div>
              ))}
            </div>
            <div className="grid-2">
              {[
                { title: 'Mission', icon: Target, text: 'To provide a reliable Forex investment and trading platform that empowers individuals to achieve financial growth through knowledge-driven strategies and transparent services.' },
                { title: 'Vision', icon: Eye, text: 'To be a globally trusted leader in the Forex trading industry, recognized for innovation, integrity, and delivering consistent value to our investors and partners.' },
              ].map(item => (
                <div key={item.title} className="info-box" style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><item.icon size={40} /></div>
                  <div className="info-box-title">{item.title}</div>
                  <p className="info-box-text">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IS FOREX */}
      <section className="section" id="forex">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">WHAT IS <span>FOREX TRADING?</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-3">
            {[
              { icon: Globe, title: 'Global Market', text: 'Forex trading is the process of buying and selling currencies in the global foreign exchange market.' },
              { icon: LineChart, title: 'Currency Pairs', text: 'It involves trading currency pairs to take advantage of exchange rate fluctuations and earn profits.' },
              { icon: Clock, title: '24/5 Market', text: 'The Forex market is the largest and most liquid financial market in the world, operating 24 hours a day, 5 days a week.' },
            ].map(item => (
              <div key={item.title} className="info-box">
                <div className="info-box-icon"><item.icon size={32} /></div>
                <div className="info-box-title">{item.title}</div>
                <p className="info-box-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRADING STRATEGIES */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TRADING <span>STRATEGIES</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2">
            {strategies.map(s => (
              <div key={s.title} className="income-type-card">
                <div className="income-type-icon"><s.icon size={24} /></div>
                <div>
                  <div className="income-type-name">{s.title}</div>
                  <div className="income-type-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }} className="card">
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <Shield size={32} style={{ flexShrink: 0, color: 'var(--primary)' }} />
              <p style={{ color: 'var(--text-light)' }}>
                <strong style={{ color: 'var(--primary)' }}>Successful trading requires</strong> market knowledge, technical analysis, disciplined risk management, and a well-planned strategy. Choose the right trading style for consistent long-term growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRADING SERVICES */}
      <section className="section" id="services">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TRADING & <span>SERVICES</span></h2>
            <div className="section-line"></div>
            <p className="section-desc">Trading is the process of buying and selling financial assets to generate profits from market price movements. It provides opportunities to grow wealth across multiple financial markets.</p>
          </div>
          <div className="grid-3">
            {services.map(s => (
              <div key={s.title} className="info-box" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><s.icon size={40} /></div>
                <div className="info-box-title">{s.title}</div>
                <p className="info-box-text">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOINING PACKAGE */}
      <section className="section section-alt" id="package">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">JOINING <span>PACKAGE</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2" style={{ maxWidth: 700, margin: '0 auto', gap: 24 }}>
            {[
              { label: 'MINIMUM AMOUNT', value: '₹500', icon: ArrowDown },
              { label: 'MAXIMUM AMOUNT', value: '₹5,00,000', icon: ArrowUp },
            ].map(item => (
              <div key={item.label} className="card" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><item.icon size={32} /></div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontFamily: 'Rajdhani', fontSize: '2.5rem', fontWeight: 700, color: 'var(--primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ maxWidth: 700, margin: '24px auto 0', borderColor: 'rgba(var(--primary-rgb),0.5)' }}>
            <div style={{ marginBottom: 12, fontWeight: 700, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 8 }}><ClipboardList size={18} /> NOTE:</div>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <li style={{ display: 'flex', gap: 10, color: 'var(--text-light)' }}><span style={{ color: 'var(--primary)' }}>▸</span> Joining will be in multiples of <strong style={{ color: 'var(--primary)' }}>₹500</strong></li>
              <li style={{ display: 'flex', gap: 10, color: 'var(--text-light)' }}><span style={{ color: 'var(--primary)' }}>▸</span> Package will be valid for <strong style={{ color: 'var(--primary)' }}>365 days</strong></li>
            </ul>
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/register" className="btn btn-primary btn-lg">Get Started Now</Link>
          </div>
        </div>
      </section>

      {/* INCOME TYPES */}
      <section className="section" id="income">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TYPES OF <span>INCOME</span></h2>
            <div className="section-line"></div>
            <p className="section-desc">Build income today, secure freedom tomorrow.</p>
          </div>
          <div className="grid-3">
            {incomeTypes.map(inc => (
              <div key={inc.title} className="income-type-card">
                <div className="income-type-icon"><inc.icon size={24} /></div>
                <div>
                  <div className="income-type-name">{inc.title}</div>
                  <div className="income-type-desc">{inc.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      

      {/* TERMS */}
      <section className="section" id="terms">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">TERMS & <span>CONDITIONS</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2">
            {[
              { icon: Calendar, text: 'ROI income will be generated Monday to Saturday' },
              { icon: CreditCard, text: 'Withdrawal available Monday to Saturday' },
              { icon: 'inr', text: 'Minimum withdrawal ₹200 (multiple of ₹100)' },
              { icon: 'percent', text: 'Withdrawal deduction 10%' },
              { icon: Clock, text: 'Withdrawal time 12:00 AM to 10:00 AM, Bank credit time 2:00 PM' },
              { icon: Landmark, text: 'Deposit and Withdrawal — INR / USDT also accepted' },
              { icon: CheckCircle, text: 'Package valid for 365 days' },
              { icon: Rocket, text: 'Capping ₹1,00,000 per day' },
            ].map(item => (
              <div key={item.text} className="income-type-card">
                <div className="income-type-icon" style={{ background: 'rgba(var(--primary-rgb),0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.icon === 'inr' ? <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>₹</span> : item.icon === 'percent' ? <span style={{ fontSize: '1.2rem', fontWeight: 700 }}>%</span> : <item.icon size={22} />}
                </div>
                <div className="income-type-desc" style={{ fontSize: '0.95rem', color: 'var(--text-light)' }}>{item.text}</div>
              </div>
            ))}
          </div>
          <div className="card" style={{ marginTop: 24, textAlign: 'center', borderColor: 'rgba(var(--primary-rgb),0.5)', background: 'linear-gradient(135deg, rgba(var(--primary-rgb),0.08), rgba(var(--secondary-rgb),0.08))' }}>
            <p style={{ color: 'var(--text-light)', fontWeight: 600, fontSize: '1.05rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Shield size={20} style={{ color: 'var(--primary)', flexShrink: 0 }} /> Follow <span style={{ color: 'var(--primary)' }}>Rules</span>, Secure Your <span style={{ color: 'var(--primary)' }}>Investment</span>, Grow with <span style={{ color: 'var(--primary)' }}>Confidence</span>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-relative" style={{ background: 'linear-gradient(135deg, rgba(var(--secondary-rgb),0.1), rgba(var(--primary-rgb),0.08))' }}>
        <div className="glow-orb-field">
          <div className="glow-orb glow-orb-gold" style={{ top: 'auto', bottom: -80, left: '10%' }}></div>
          <div className="glow-orb glow-orb-blue" style={{ top: -60, right: '15%', bottom: 'auto' }}></div>
        </div>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Rajdhani', fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>
            TOGETHER WE TRADE,<br /><span style={{ color: 'var(--primary)' }}>TOGETHER WE GROW</span>
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
            Join thousands of investors building their financial future with Triveni Pay. Start with just ₹500 today.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-primary btn-lg">Join Now It's Free</Link>
            <Link to="/login" className="btn btn-outline btn-lg">Login</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}